import type { CameraState } from './types'

/**
 * A sensible starting point: A mode at 35mm on an overcast afternoon, which
 * is where most people actually leave this camera.
 */
export const DEFAULT_CAMERA_STATE: CameraState = {
  mode: 'A',
  shutterDial: '1/125',
  isoDial: '400',
  expCompDial: 0,
  photoVideo: 'photo',
  mainCommandDialShutterSeconds: 1 / 125,
  subCommandDialAperture: 5.6,
  commandDialExpComp: 0,
  focalLength: 35,
  sceneEv100: 12,
}

export function withState(patch: Partial<CameraState>): CameraState {
  return { ...DEFAULT_CAMERA_STATE, ...patch }
}

/**
 * Named light levels, so scenarios read like the world instead of numbers.
 *
 * Each label describes something you can SEE. "Golden hour" was a time of day
 * sitting in a list of brightnesses — people picked it at 8pm in June when it
 * was still bright — so it is anchored to how dim it looks instead.
 */
export const SCENE_PRESETS = [
  { id: 'sun', label: 'Bright sun', ev: 15, hint: 'Outdoors, hard shadows' },
  { id: 'overcast', label: 'Overcast', ev: 12, hint: 'Outdoors, flat grey daylight' },
  { id: 'shade', label: 'Open shade', ev: 11, hint: 'Under a tree or a building' },
  { id: 'golden', label: 'Low sun', ev: 9, hint: 'Late in the day — warm, and noticeably dim' },
  { id: 'indoor', label: 'Indoors, daytime', ev: 8, hint: 'Daylight through a window, lamps off' },
  { id: 'lamp', label: 'Indoors, evening', ev: 6, hint: 'Lamps on, curtains shut' },
  { id: 'dim', label: 'Bar or restaurant', ev: 5, hint: 'Low warm light, candles on the table' },
  { id: 'street', label: 'Street at night', ev: 4, hint: 'Shop windows, streetlights' },
] as const
